<script setup lang="ts">
import { parseBold } from "~/helpers/functions/TextUtils";

import { useIsProService } from "~/helpers/functions/IsProService";

const isProService = useIsProService();
const refResponses = ref<Array<boolean>>(new Array(7).fill(false));
const beforeLeave = (el: Element) => {
  const element = el as HTMLElement;
  element.style.maxHeight = element.scrollHeight + "px";
  element.style.opacity = "1";
};

const onLeave = (el: Element, done: () => void) => {
  const element = el as HTMLElement;
  const duration = 1000;
  element.style.transition = `all ${duration}ms ease`;
  element.style.maxHeight = "0px";
  element.style.opacity = "0";
  setTimeout(() => {
    done();
  }, duration);
};
/**
 * Toggle the boolean value at the given index in the refResponses array.
 *
 * @param {number} index The index of the value to toggle.
 */
const openResponse = (index: number) => {
  const selection = window.getSelection();
  if (selection && selection.toString().length > 0) {
    return;
  }
  refResponses.value[index] = !refResponses.value[index];
};
const { data } = defineProps<{
  data: {
    text: string;
    questions: {
      question: string;
      answer: Array<{ type: string; value: string | Array<string> }>;
    }[];
  };
}>();
</script>
<template>
  <section class="question">
    <div class="top">
      <h2 class="subTitle">{{ $t("UiServiceDetail.questions.title") }}</h2>
      <p class="text">{{ data.text }}</p>
    </div>
    <div class="bottom">
      <div
        v-for="(item, index) in data.questions"
        :key="index"
        class="list"
        :index="index"
        :class="{ lastChild: index === data.questions.length - 1 }"
        @click="openResponse(index - 1)"
      >
        <div class="list-item">
          <p class="list-title cursor-hover">
            {{ item.question }}
          </p>
          <img
            v-if="!isProService"
            class="plus cursor-hover"
            :class="{ active: refResponses[index - 1] }"
            src="@/assets/img/custom/plus.svg"
            :alt="$t('alt.plus')"
          />
          <img
            v-if="isProService"
            class="plus cursor-hover"
            :class="{ active: refResponses[index - 1] }"
            src="@/assets/img/custom/blue-plus.svg"
            :alt="$t('alt.plus')"
          />
        </div>
        <transition
          name="answer-content"
          @before-leave="beforeLeave"
          @leave="onLeave"
        >
          <div v-if="refResponses[index - 1]" class="responseContainer">
            <template
              v-for="(answer, answerIndex) in item.answer"
              :key="answerIndex"
            >
              <p
                v-if="
                  answer.type === 'sentence' && typeof answer.value === 'string'
                "
                class="response"
                v-html="parseBold(answer.value)"
              ></p>
              <ul v-else-if="answer.type === 'list'" class="responseList">
                <li
                  v-for="(listElement, listElementIndex) in answer.value"
                  :key="listElementIndex"
                >
                  <p class="response" v-html="parseBold(listElement)"></p>
                </li>
              </ul>
            </template>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>
<style scoped>
section {
  padding-top: 8rem;
  padding-bottom: 8rem;
  display: grid;
  grid-template-columns: 0.75fr 1fr;
  margin: 0 3.8rem;
  gap: 2rem;
}

.bottom {
  width: 81%;
}

.list {
  border-top: 1px solid black;
}
li {
  list-style-type: none;
}
.list div {
  display: flex;
  gap: 3rem;
  justify-content: space-between;
  align-items: center;
}

.answer-content-enter-from,
.answer-content-leave-to {
  opacity: 0;
  max-height: 0;
}

.answer-content-enter-active,
.answer-content-leave-active {
  transition: all 1.7s ease;
}

.answer-content-enter-to,
.answer-content-leave-from {
  opacity: 1;
  max-height: 1000px;
}

.responseContainer {
  display: flex;
  flex-direction: column;
  gap: 1rem !important;
  padding-left: 1.5rem;
  padding-bottom: 1rem;
}

.response,
.responseList {
  overflow: hidden;
  font-family: "Gilmer Regular", sans-serif;
  margin: 0;
  /* Add padding if needed for spacing */
}

.responseList {
  padding-left: 1em;
}

.subTitle {
  color: var(--vt-c-medium-purple);
  text-align: center;
  font: normal normal normal 48px/67px "Gilmer Bold";
  margin-top: 0;
  margin-bottom: 0;
}
.lastChild {
  border-bottom: 1px solid black;
}
.top {
  margin-bottom: 4rem;
}
.top .text {
  font: normal normal normal 22px/29px "Gilmer Regular";
  text-align: center;
  margin: auto;
  width: 363px;
}
.plus {
  width: 21px;
  height: 21px;
  transition: all 0.3s ease;
}
.active {
  transform: rotate(45deg);
}
.list-title,
.response,
.responseList {
  font: normal normal normal 22px/29px "Gilmer Medium";
  text-align: left;
}
.response,
.responseList {
  font-family: "Gilmer Regular", sans-serif;
}
.responseContainer {
  margin-bottom: 1rem;
}
@media screen and (max-width: 1200px) {
  section {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}
@media screen and (max-width: 1125px) {
  section {
    margin: 0 2rem;
    padding: unset;
    padding-top: 3rem;
    padding-bottom: 4rem;
    display: block;
  }
  .bottom {
    width: 100%;
  }
  .subTitle {
    font-size: 25px;
    line-height: 67px;
    margin-bottom: 0;
  }
  .list-title,
  .response {
    font-size: 17px;
    line-height: 20px;
  }

  .top .text {
    font-size: 17px;
    line-height: 23px;
    width: unset;
    padding: 0 57px;
    margin: auto;
  }
}
</style>
